import {
  rechargeReceiptRemote,
  unRechargeReceiptRemote,
  type RemoteJob,
} from '../api/client.js';
import { clientNeedsXeroAttention } from '../clients/xeroStatus.js';
import {
  dateOnly,
  deleteJobLocal,
  listClientsCached,
  listSitesCached,
  putJobLocal,
  touchClientUsed,
  type CachedClient,
  type CachedSite,
  type LocalJob,
} from '../db/jobsDb.js';
import { putReceiptLocal, type LocalReceipt } from '../db/receiptsDb.js';
import { sterlingNetPence } from '../../../src/receipts/currency.js';

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatPence(p: number | null | undefined): string {
  if (p == null) return '—';
  return `£${(p / 100).toFixed(2)}`;
}

function clientOptionsHtml(
  clients: CachedClient[],
  selected: string | null,
): string {
  if (clients.length === 0) {
    return '<option value="">No clients cached — sign in when online</option>';
  }
  return clients
    .map((c) => {
      const mark = clientNeedsXeroAttention(c) ? ' · not in Xero' : '';
      const sel = c.id === selected ? ' selected' : '';
      return `<option value="${escapeHtml(c.id)}"${sel}>${escapeHtml(c.name)}${mark}</option>`;
    })
    .join('');
}

function siteOptionsHtml(
  sites: CachedSite[],
  clientId: string | null,
  selected: string | null,
): string {
  const filtered = sites.filter(
    (s) => !clientId || s.client_id === null || s.client_id === clientId,
  );
  const opts = filtered
    .map((s) => {
      const sel = s.id === selected ? ' selected' : '';
      return `<option value="${escapeHtml(s.id)}"${sel}>${escapeHtml(s.name)}</option>`;
    })
    .join('');
  return `<option value="">No site</option>${opts}`;
}

export function rechargeSectionHtml(
  item: LocalReceipt,
  clients: CachedClient[],
  sites: CachedSite[],
): string {
  if (item.status !== 'confirmed' && item.status !== 'sent_to_accounts') {
    return '';
  }
  const client = clients.find((c) => c.id === item.client_id);
  const netGbp = sterlingNetPence({
    currency: item.currency,
    net_pence: item.net_pence,
    gbp_net_pence: item.gbp_net_pence ?? null,
  });
  if (item.recharge_job_id) {
    const frozen = Boolean(item.recharge_frozen);
    return `<section class="receipt-recharge" data-recharge-linked>
      <h2>Recharged to a client</h2>
      <p>${escapeHtml(client?.name ?? 'Client')} · ${formatPence(netGbp)} net</p>
      <p class="hint">Expense job on ${escapeHtml(item.receipt_date ?? 'the receipt date')}. Week sign-off still required before Saturday invoicing.</p>
      <button type="button" class="btn-secondary btn-block" data-open-recharge-job>View job</button>
      ${
        frozen
          ? `<p class="hint">Invoiced — this recharge cannot be removed.</p>`
          : `<button type="button" class="btn-secondary btn-block" data-unrecharge>Remove recharge</button>`
      }
    </section>`;
  }

  const prominent = item.rechargeable !== false;
  const picker = `<label class="field"><span>Client</span>
      <select name="recharge_client_id" data-recharge-client>
        ${clientOptionsHtml(clients, item.client_id ?? null)}
      </select>
    </label>
    <label class="field"><span>Site (optional)</span>
      <select name="recharge_site_id" data-recharge-site>
        ${siteOptionsHtml(sites, item.client_id ?? clients[0]?.id ?? null, item.site_id ?? null)}
      </select>
    </label>
    ${
      netGbp == null
        ? `<p class="hint">Add a net amount before recharging — the job is billed at sterling net.</p>`
        : ''
    }
    <button type="button" class="submit" data-recharge-yes>Recharge</button>
    ${
      prominent
        ? `<button type="button" class="btn-secondary btn-block" data-recharge-skip>Not this time</button>`
        : ''
    }`;

  if (!prominent) {
    return `<section class="receipt-recharge is-compact" data-recharge>
      <h2>Recharge to a client?</h2>
      <p class="hint">Creates an expense job at ${formatPence(netGbp)} net for Saturday invoicing.</p>
      <button type="button" class="btn-secondary btn-block" data-recharge-expand>Recharge to a client</button>
      <div data-recharge-picker hidden>${picker}</div>
    </section>`;
  }

  return `<section class="receipt-recharge" data-recharge>
    <h2>Recharge to a client?</h2>
    <p class="hint">Creates an expense job at ${formatPence(netGbp)} net. It stays off the Saturday run until you sign the week off.</p>
    ${picker}
  </section>`;
}

function applyReceiptRecharge(
  cur: LocalReceipt,
  remote: {
    rechargeable?: boolean | null;
    client_id?: string | null;
    site_id?: string | null;
    recharge_job_id?: string | null;
    recharge_frozen?: boolean;
  },
): LocalReceipt {
  return {
    ...cur,
    rechargeable: remote.rechargeable ?? null,
    client_id: remote.client_id ?? null,
    site_id: remote.site_id ?? null,
    recharge_job_id: remote.recharge_job_id ?? null,
    recharge_frozen: remote.recharge_frozen ?? false,
  };
}

export function localJobFromRecharge(
  job: RemoteJob,
  receiptId: string,
): LocalJob {
  const now = new Date().toISOString();
  return {
    id: job.id,
    work_date: dateOnly(job.work_date) ?? String(job.work_date).slice(0, 10),
    client_id: job.client_id,
    work_type_id: job.work_type_id,
    duration_type: job.duration_type,
    custom_hours: job.custom_hours == null ? null : Number(job.custom_hours),
    custom_amount: job.custom_amount == null ? null : Number(job.custom_amount),
    site_reference: job.site_reference,
    site_ids: Array.isArray(job.site_ids) ? job.site_ids : [],
    billable: job.billable !== false,
    status: job.status,
    start_time: job.start_time,
    confirmed_at: job.confirmed_at,
    released_at: job.released_at ?? null,
    xero_invoice_id: job.xero_invoice_id ?? null,
    po_number: job.po_number,
    notes: job.notes,
    syncState: 'synced',
    failReason: null,
    invoice_run_id: job.invoice_run_id,
    invoice_number: job.invoice_number ?? null,
    deleted_at: null,
    pendingOp: null,
    createdAt: job.created_at,
    updatedAt: job.updated_at,
    lastSyncAttemptAt: now,
    recharge_receipt_id: receiptId,
  };
}

export async function loadRechargeLists(): Promise<{
  clients: CachedClient[];
  sites: CachedSite[];
}> {
  const [clients, sites] = await Promise.all([
    listClientsCached(),
    listSitesCached(),
  ]);
  return { clients, sites };
}

export function bindRechargeSection(
  root: HTMLElement,
  opts: {
    item: LocalReceipt;
    clients: CachedClient[];
    sites: CachedSite[];
    onChange: () => Promise<void>;
    onOpenJob?: (id: string) => void;
    onError: (text: string) => void;
  },
): void {
  const { item } = opts;
  const clientSelect = root.querySelector(
    '[data-recharge-client]',
  ) as HTMLSelectElement | null;
  const siteSelect = root.querySelector(
    '[data-recharge-site]',
  ) as HTMLSelectElement | null;

  clientSelect?.addEventListener('change', () => {
    if (!siteSelect) return;
    siteSelect.innerHTML = siteOptionsHtml(
      opts.sites,
      clientSelect.value || null,
      null,
    );
  });

  root.querySelector('[data-recharge-expand]')?.addEventListener('click', () => {
    const picker = root.querySelector('[data-recharge-picker]') as HTMLElement | null;
    if (picker) picker.hidden = false;
  });

  root.querySelector('[data-open-recharge-job]')?.addEventListener('click', () => {
    if (item.recharge_job_id) opts.onOpenJob?.(item.recharge_job_id);
  });

  root.querySelector('[data-recharge-yes]')?.addEventListener('click', () => {
    void (async () => {
      const clientId = clientSelect?.value;
      if (!clientId) {
        opts.onError('Pick a client to recharge this receipt.');
        return;
      }
      if (item.net_pence == null) {
        opts.onError('Add a net amount before recharging.');
        return;
      }
      try {
        const siteId = siteSelect?.value || null;
        const result = await rechargeReceiptRemote(item.id, {
          client_id: clientId,
          site_id: siteId,
        });
        const cur = { ...item };
        await putReceiptLocal(applyReceiptRecharge(cur, result.receipt));
        if (result.job) {
          await putJobLocal(localJobFromRecharge(result.job, item.id));
          await touchClientUsed(result.job.client_id);
        }
        await opts.onChange();
      } catch (err) {
        opts.onError(
          err instanceof Error ? err.message : 'Could not recharge this receipt.',
        );
      }
    })();
  });

  root.querySelector('[data-recharge-skip]')?.addEventListener('click', () => {
    void (async () => {
      try {
        const result = await rechargeReceiptRemote(item.id, { skip: true });
        await putReceiptLocal(applyReceiptRecharge(item, result.receipt));
        await opts.onChange();
      } catch (err) {
        opts.onError(
          err instanceof Error ? err.message : 'Could not skip recharge.',
        );
      }
    })();
  });

  root.querySelector('[data-unrecharge]')?.addEventListener('click', () => {
    void (async () => {
      const ok = window.confirm(
        'Remove this recharge? The expense job is deleted if it has not been invoiced.',
      );
      if (!ok) return;
      try {
        const jobId = item.recharge_job_id;
        const result = await unRechargeReceiptRemote(item.id);
        await putReceiptLocal(applyReceiptRecharge(item, result.receipt));
        if (jobId) await deleteJobLocal(jobId);
        await opts.onChange();
      } catch (err) {
        opts.onError(
          err instanceof Error ? err.message : 'Could not remove recharge.',
        );
      }
    })();
  });
}
